import React from 'react';
import { pickLocalized, getPaperProgress } from '../../utils/testUtils';
import { normalizeAbilitySlug } from '../../data/abilityPapers';

const CheckIcon = () => (
  <span
    aria-hidden
    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm"
  >
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="m5 12 4 4 10-10" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const PART_STATUS_STYLES = {
  complete: {
    cell: 'bg-emerald-50 hover:bg-emerald-100 focus-visible:ring-emerald-500 dark:bg-emerald-950/40 dark:hover:bg-emerald-950/60',
    badge: 'bg-emerald-600 text-white dark:bg-emerald-500',
    title: 'text-emerald-950 dark:text-emerald-100'
  },
  inProgress: {
    cell: 'bg-amber-50 hover:bg-amber-100 focus-visible:ring-amber-600 dark:bg-amber-950/40 dark:hover:bg-amber-950/60',
    badge: 'bg-amber-700 text-white dark:bg-amber-500',
    title: 'text-amber-950 dark:text-amber-100'
  },
  notStarted: {
    cell: 'bg-surface hover:bg-surface-muted focus-visible:ring-ring',
    badge: 'bg-primary text-primary-foreground',
    title: 'text-ink-muted'
  },
  locked: {
    cell: 'bg-canvas opacity-70 cursor-not-allowed',
    badge: 'bg-edge text-ink-muted',
    title: 'text-ink-subtle'
  },
  soon: {
    cell: 'bg-canvas opacity-70 cursor-not-allowed',
    badge: 'bg-edge text-ink-muted',
    title: 'text-ink-subtle'
  }
};

function getPartStatus(paper, index, completedSlugs, progressBySlug, firstUnfinishedIndex) {
  const slug = normalizeAbilitySlug(paper.slug);
  if (paper.comingSoon) return 'soon';
  if (completedSlugs?.has?.(slug)) return 'complete';
  if (firstUnfinishedIndex >= 0 && index > firstUnfinishedIndex) return 'locked';
  const { answered } = getPaperProgress(paper, progressBySlug);
  if (answered > 0) return 'inProgress';
  return 'notStarted';
}

function isPartClickable(paper, status) {
  if (paper.comingSoon || status === 'locked' || status === 'soon') return false;
  return true;
}

function AbilityPreparationPart({
  paper,
  index,
  lang,
  t,
  status,
  clickable,
  onSelect
}) {
  const styles = PART_STATUS_STYLES[clickable ? status : status === 'soon' ? 'soon' : 'locked'];
  const title = pickLocalized(paper.title, lang);
  const statusLabel =
    status === 'complete'
      ? t('test.chapterComplete')
      : status === 'inProgress'
        ? t('test.inProgress')
        : status === 'soon'
          ? t('test.comingSoon')
          : t('test.notStarted');

  const sharedClass = `flex min-h-28 flex-col items-center justify-center gap-3 px-3 py-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
    index > 0 ? 'border-l border-edge' : ''
  } ${styles.cell}`;

  const content = (
    <>
      <span
        className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold shadow-sm ${styles.badge}`}
      >
        {status === 'complete' ? (
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
            <path d="m5 12 4 4 10-10" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          index + 1
        )}
      </span>
      <span className={`text-sm font-semibold md:text-base ${styles.title}`}>{title}</span>
      <span className="sr-only">{statusLabel}</span>
    </>
  );

  if (!clickable) {
    return (
      <li
        aria-disabled="true"
        className={`${sharedClass} ${index === 4 ? 'col-span-2 sm:col-span-1' : ''}`}
      >
        {content}
      </li>
    );
  }

  return (
    <li className={index === 4 ? 'col-span-2 sm:col-span-1' : ''}>
      <button
        type="button"
        onClick={() => onSelect(paper)}
        aria-label={`${title} — ${statusLabel}`}
        className={`w-full ${sharedClass}`}
      >
        {content}
      </button>
    </li>
  );
}

function AbilityPreparationPage({
  t,
  lang,
  papers,
  completedSlugs,
  progressBySlug,
  firstUnfinishedIndex,
  onSelectPart,
  onPrepared,
  onRestart,
  onRetake,
  hasOngoingTest = false,
  disabled = false
}) {
  const reminders = [
    t('test.preparationQuiet'),
    t('test.preparationReadOptions'),
    t('test.preparationNoHelp'),
    t('test.preparationNoRefresh')
  ];

  return (
    <section className="mx-auto flex w-full max-w-4xl flex-col items-center px-4 py-10 text-center md:px-8 md:py-14">
      <header className="max-w-3xl">
        <h1 className="font-montserrat text-4xl font-bold tracking-tight text-ink md:text-5xl">
          {t('test.preparationTitle')}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-base leading-7 text-ink-muted md:text-lg md:leading-8">
          {t('test.preparationIntro')}
        </p>
      </header>

      <div className="mt-9 grid w-full max-w-2xl gap-3 text-left">
        {reminders.map((reminder) => (
          <div
            key={reminder}
            className="flex min-h-16 items-center gap-4 rounded-lg border border-edge bg-surface px-5 py-4 text-base font-medium text-ink-muted shadow-sm md:text-lg"
          >
            <CheckIcon />
            <span>{reminder}</span>
          </div>
        ))}
      </div>

      <ol className="mt-10 grid w-full max-w-4xl grid-cols-2 overflow-hidden rounded-lg border border-edge bg-surface shadow-sm sm:grid-cols-5">
        {papers.map((paper, index) => {
          const status = getPartStatus(
            paper,
            index,
            completedSlugs,
            progressBySlug,
            firstUnfinishedIndex
          );
          const clickable = isPartClickable(paper, status);

          return (
            <AbilityPreparationPart
              key={paper.slug}
              paper={paper}
              index={index}
              lang={lang}
              t={t}
              status={status}
              clickable={clickable}
              onSelect={onSelectPart}
            />
          );
        })}
      </ol>

      {disabled ? (
        <div className="mt-10 flex flex-col items-center gap-4">
          <p className="text-base font-semibold text-link md:text-lg">
            {t('test.allAbilitiesComplete')}
          </p>
          {onRetake ? (
            <button
              type="button"
              onClick={onRetake}
              className="min-w-72 rounded-full border border-primary bg-surface px-10 py-3.5 text-base font-semibold text-primary shadow-sm transition-colors hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-canvas"
            >
              {t('test.retakeAbilityTest')}
            </button>
          ) : null}
        </div>
      ) : (
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={onPrepared}
            className="min-w-72 rounded-full bg-primary px-10 py-3.5 text-base font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-canvas"
          >
            {hasOngoingTest ? t('test.resumeTest') : t('test.wellPrepared')}
          </button>
          {hasOngoingTest && onRestart ? (
            <button
              type="button"
              onClick={onRestart}
              className="min-w-72 rounded-full border border-primary bg-surface px-10 py-3.5 text-base font-semibold text-primary shadow-sm transition-colors hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-canvas"
            >
              {t('test.restartAbilityTest')}
            </button>
          ) : null}
        </div>
      )}

      <p className="mt-7 flex items-center justify-center gap-3 text-sm text-ink-subtle md:text-base">
        <svg aria-hidden viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M7 18h10a4 4 0 0 0 .4-7.98A6 6 0 0 0 6.1 8.6 4.8 4.8 0 0 0 7 18Z" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span>{t('test.preparationAutosave')}</span>
      </p>
    </section>
  );
}

export default AbilityPreparationPage;
